import { assertUnreachable, compareByString, isPresent, uniq, uniqBy } from '@luminovo/commons';

import type { IconElement } from '../../icons/IconElement';
import { Argument, OperatorIn, OperatorIs, OperatorLike, ParameterOption } from '../types';

export type Operators = 'like' | 'in' | 'not in' | 'is';

export function createOperators<T, TAutocompleteState, K extends string>({
    operators,
    getItems,
    formatter,
    extractor,
    getLabel = () => undefined,
}: {
    operators: Array<Operators>;
    getItems: (autocompleteState?: TAutocompleteState) => T[];
    formatter: (item: T, autocompleteState?: TAutocompleteState) => string;
    extractor: (item: T) => K | null;
    getLabel?: (op: Operators) => string | undefined;
}): (OperatorIn<T, TAutocompleteState> | OperatorIs<T, TAutocompleteState, Argument> | OperatorLike<T>)[] {
    const generateOptions = (autocompleteState?: TAutocompleteState) => {
        return uniqBy(
            getItems(autocompleteState)
                .map((item) => {
                    return {
                        value: String(extractor(item)),
                        label: formatter(item, autocompleteState),
                    };
                })
                .sort((a, b) => compareByString(a.label, b.label)),
            'value',
        );
    };

    return operators.map(
        (op): OperatorIn<T, TAutocompleteState> | OperatorIs<T, TAutocompleteState, Argument> | OperatorLike<T> => {
            switch (op) {
                case 'like':
                    return {
                        op: 'like',
                        label: getLabel(op),
                        filter: (item, parameter) => {
                            const key = extractor(item);
                            return isPresent(key) && key.toLowerCase().includes(parameter.toLowerCase());
                        },
                    };
                case 'is':
                    return {
                        op: 'is',
                        options: generateOptions,
                        label: getLabel(op),
                        filter: (item, parameter) => {
                            return parameter === extractor(item);
                        },
                    };
                case 'in':
                    return {
                        op: 'in',
                        options: generateOptions,
                        label: getLabel(op),
                        filter: (item, parameter) => {
                            const key = extractor(item);
                            return isPresent(key) && parameter.includes(key);
                        },
                    };
                case 'not in':
                    return {
                        op: 'not in',
                        options: generateOptions,
                        label: getLabel(op),
                        filter: (item, parameter) => {
                            const key = extractor(item);
                            return isPresent(key) && !parameter.includes(key);
                        },
                    };
                default:
                    assertUnreachable(op);
            }
        },
    );
}

/** @experimental A `ParameterOption` for the `AutocompleteOptions`; may change. */
export function createParameterOption<T, TAutocompleteState, K extends string>({
    icon,
    field,
    operators,
    getItems,
    formatter,
    extractor,
    getLabel,
}: {
    icon: IconElement;
    field: string;
    operators: Array<Operators>;
    getItems: (autocompleteState?: TAutocompleteState) => T[];
    formatter: (item: T, autocompleteState?: TAutocompleteState) => string;
    extractor: (item: T) => K | null;
    getLabel?: (op: Operators) => string | undefined;
}): ParameterOption<T, TAutocompleteState> {
    return {
        icon,
        field,
        formatter: (parameter, state) => {
            if (Array.isArray(parameter)) {
                const filteredLabels = getItems(state)
                    .filter((item) => {
                        const key = extractor(item);
                        return isPresent(key) && parameter.includes(key);
                    })
                    .map((item) => formatter(item));

                if (filteredLabels.length === 0) {
                    return parameter.join(', ');
                }

                return uniq(filteredLabels).sort(compareByString).join(', ');
            } else {
                return String(parameter);
            }
        },
        ops: createOperators({
            operators,
            getItems,
            formatter,
            extractor,
            getLabel,
        }),
    };
}
